import {Phone, Calendar, Shield, Sparkles, Check, MapPin, Award, Car, Droplets, Sun, Clock, X, Layers, Zap} from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { useEffect } from 'react'
import CanonicalTag from '@/components/CanonicalTag'
import EmbeddedQuoteForm from '@/components/EmbeddedQuoteForm'
import BeforeAfterSlider from '@/components/BeforeAfterSlider'

const PpfVsCeramicCoatingSanDiegoPage = () => {
  const navigate = useNavigate()

  useEffect(() => {
    document.title = 'PPF vs Ceramic Coating San Diego | Which Is Better?'
    const metaDesc = document.querySelector('meta[name="description"]')
    if (metaDesc) {
      metaDesc.setAttribute('content', 'PPF vs Ceramic Coating in San Diego — which should you choose? Compare paint protection, rock chip defense, self-healing, UV protection, hydrophobic properties, longevity, cost, and warranty. Learn why many Tesla and luxury owners choose both. Call 858-282-8119.')
    }

    const faqScript = document.createElement('script')
    faqScript.type = 'application/ld+json'
    faqScript.text = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: [
        {
          '@type': 'Question',
          name: 'Is PPF better than ceramic coating?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'Neither is universally "better" — they do different jobs. Paint Protection Film (PPF) is a thick urethane film that physically blocks rock chips, scratches, and impact, and it self-heals light marks. Ceramic coating is a thin chemical layer that adds gloss, UV protection, and hydrophobic easy-cleaning but does not stop rock chips. For maximum protection, PPF wins on physical defense; ceramic wins on shine and maintenance. Many San Diego owners use both together.'
          }
        },
        {
          '@type': 'Question',
          name: 'Do I need both PPF and ceramic coating?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'You do not strictly need both, but combining them gives the most complete protection. PPF protects your paint from rock chips and impact, while a ceramic coating applied over the PPF and the rest of the vehicle adds gloss, UV resistance, and a hydrophobic, easy-clean surface. This PPF-plus-ceramic combination is the most popular choice among Tesla and luxury vehicle owners in San Diego.'
          }
        },
        {
          '@type': 'Question',
          name: 'Does ceramic coating stop rock chips?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'No. Ceramic coating is only microns thick and is a chemical layer, not a physical barrier. It resists light swirls, chemical stains, and UV damage, but it cannot stop rock chips, road debris, or deep scratches. Only Paint Protection Film (PPF) provides genuine rock chip and impact protection.'
          }
        },
        {
          '@type': 'Question',
          name: 'Can ceramic coating replace PPF?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'No. Ceramic coating and PPF solve different problems. Ceramic coating cannot replace the physical rock chip and impact protection that Paint Protection Film provides. If your main goal is preventing chips, scratches, and road damage, you need PPF. Ceramic coating is best used as a complement — often applied over PPF — rather than a substitute.'
          }
        },
        {
          '@type': 'Question',
          name: 'Which lasts longer, PPF or ceramic coating?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'PPF lasts longer. Quality Paint Protection Film typically lasts 7 to 10 years and often carries a manufacturer warranty. Ceramic coating generally lasts 2 to 5 years depending on the product and care. Because PPF is more durable and warranty-backed, it is the longer-term protection investment, while ceramic is refreshed more frequently.'
          }
        },
        {
          '@type': 'Question',
          name: 'Which is worth the money, PPF or ceramic coating?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'It depends on your goals. PPF is worth it if you want to protect your paint from rock chips and preserve resale value on a new, leased, or long-term vehicle — especially Teslas and luxury cars. Ceramic coating is worth it if you want a glossy, low-maintenance, easy-to-clean finish at a lower investment. For the best long-term value, many San Diego owners invest in PPF on high-impact areas and ceramic coating over the whole vehicle.'
          }
        },
        {
          '@type': 'Question',
          name: 'What do most Tesla owners choose, PPF or ceramic coating?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'Most Tesla owners in San Diego choose PPF on high-impact areas — or full-front and full-body PPF — because Tesla factory paint is soft and chips easily, then add a ceramic coating on top for gloss and easy maintenance. The PPF-plus-ceramic combination is the go-to package for Model Y, Model 3, Model S, and Model X owners who want both protection and a flawless, low-maintenance finish.'
          }
        },
        {
          '@type': 'Question',
          name: 'Can ceramic coating be applied over PPF?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'Yes — and it is highly recommended. Applying a ceramic coating over Paint Protection Film enhances the film\'s hydrophobic properties, reduces staining, boosts gloss, and makes the PPF easier to clean. The ceramic layer acts as a sacrificial, easy-maintenance topcoat that protects and beautifies the PPF underneath. It is one of the most popular finishing steps we perform in San Diego.'
          }
        }
      ]
    })
    document.head.appendChild(faqScript)

    const serviceScript = document.createElement('script')
    serviceScript.type = 'application/ld+json'
    serviceScript.text = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'Service',
      serviceType: 'Paint Protection Film and Ceramic Coating',
      name: 'PPF vs Ceramic Coating San Diego — San Diego PPF Landing Page',
      description: 'Expert comparison of Paint Protection Film (PPF) and ceramic coating in San Diego. Professional PPF and ceramic coating installation for paint protection, rock chip defense, gloss, UV protection, and easy maintenance.',
      areaServed: ['San Diego', 'Vista', 'Oceanside', 'Carlsbad', 'San Marcos', 'Escondido', 'Encinitas', 'Poway', 'Del Mar', 'La Jolla', 'Rancho Santa Fe', 'North County San Diego'],
      provider: {
        '@type': 'LocalBusiness',
        name: 'Mr Wrap - AutoWrapSanDiego',
        telephone: '+1-858-282-8119',
        priceRange: '$$$',
        url: 'https://autowrapsandiego.com',
        address: {
          '@type': 'PostalAddress',
          streetAddress: '2244 South Santa Fe Avenue, Suite A7',
          addressLocality: 'Vista',
          addressRegion: 'CA',
          postalCode: '92084',
          addressCountry: 'US'
        },
        areaServed: 'San Diego County'
      }
    })
    document.head.appendChild(serviceScript)

    const localBusinessScript = document.createElement('script')
    localBusinessScript.type = 'application/ld+json'
    localBusinessScript.text = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'LocalBusiness',
      name: 'Mr Wrap - AutoWrapSanDiego',
      image: 'https://autowrapsandiego.com/lumi.ing/logo.png',
      telephone: '+1-858-282-8119',
      url: 'https://autowrapsandiego.com/ppf-vs-ceramic-coating-san-diego',
      priceRange: '$$$',
      address: {
        '@type': 'PostalAddress',
        streetAddress: '2244 South Santa Fe Avenue, Suite A7',
        addressLocality: 'Vista',
        addressRegion: 'CA',
        postalCode: '92084',
        addressCountry: 'US'
      },
      areaServed: ['San Diego', 'Vista', 'Oceanside', 'Carlsbad', 'San Marcos', 'Escondido', 'Encinitas', 'Poway', 'Del Mar', 'La Jolla', 'Rancho Santa Fe', 'North County San Diego', 'San Diego County'],
      openingHoursSpecification: [
        { '@type': 'OpeningHoursSpecification', dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'], opens: '09:00', closes: '17:00' },
        { '@type': 'OpeningHoursSpecification', dayOfWeek: ['Saturday'], opens: '09:00', closes: '15:00' }
      ]
    })
    document.head.appendChild(localBusinessScript)

    const breadcrumbScript = document.createElement('script')
    breadcrumbScript.type = 'application/ld+json'
    breadcrumbScript.text = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://autowrapsandiego.com/' },
        { '@type': 'ListItem', position: 2, name: 'Paint Protection Film San Diego', item: 'https://autowrapsandiego.com/paint-protection-film-san-diego' },
        { '@type': 'ListItem', position: 3, name: 'PPF vs Ceramic Coating San Diego', item: 'https://autowrapsandiego.com/ppf-vs-ceramic-coating-san-diego' }
      ]
    })
    document.head.appendChild(breadcrumbScript)

    return () => {
      document.head.removeChild(faqScript)
      document.head.removeChild(serviceScript)
      document.head.removeChild(localBusinessScript)
      document.head.removeChild(breadcrumbScript)
    }
  }, [])

  return (
    <>
      <CanonicalTag url="/ppf-vs-ceramic-coating-san-diego" />
      <div className="pt-20 min-h-screen bg-black">

        {/* Hero */}
        <section className="bg-gradient-to-br from-black via-gray-900 to-black text-white py-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center">
              <h1 className="text-4xl sm:text-5xl font-bold mb-6 leading-tight">
                PPF vs Ceramic Coating San Diego
              </h1>
              <p className="text-xl text-gray-300 max-w-3xl mx-auto mb-4">
                Should you get Paint Protection Film or Ceramic Coating? In short: <strong className="text-white">PPF physically protects your paint</strong> from rock chips and scratches, while <strong className="text-white">ceramic coating adds gloss, UV protection, and an easy-clean hydrophobic finish</strong>. They solve different problems — and many San Diego owners get both.
              </p>
              <p className="text-lg text-gray-400 max-w-3xl mx-auto mb-8">
                This is the definitive PPF vs ceramic coating guide for San Diego drivers. Serving Vista, Oceanside, Carlsbad, San Marcos, Escondido, Encinitas, Poway, Del Mar, La Jolla, Rancho Santa Fe, and all of San Diego County.
              </p>
              <div className="flex gap-4 justify-center flex-wrap">
                <a href="tel:858-282-8119" className="flex items-center gap-2 bg-cyan-500 hover:bg-cyan-600 text-white px-6 py-3 rounded-lg font-semibold transition">
                  <Phone size={18} />
                  858-282-8119
                </a>
                <button onClick={() => { navigate('/contact'); setTimeout(() => window.scrollTo(0, 0), 100) }} className="flex items-center gap-2 bg-orange-500 hover:bg-orange-600 text-white px-6 py-3 rounded-lg font-semibold transition">
                  <Calendar size={18} />
                  Get a Free Quote
                </button>
              </div>
            </div>
          </div>
        </section>

        {/* Breadcrumb */}
        <nav aria-label="Breadcrumb" className="bg-black border-b border-gray-800">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3">
            <ol className="flex items-center gap-2 text-sm text-gray-400 flex-wrap">
              <li><a href="/" className="hover:text-cyan-400 transition">Home</a></li>
              <li className="text-gray-600">›</li>
              <li><a href="/paint-protection-film-san-diego" className="hover:text-cyan-400 transition">Paint Protection Film San Diego</a></li>
              <li className="text-gray-600">›</li>
              <li className="text-cyan-400 font-medium" aria-current="page">PPF vs Ceramic Coating San Diego</li>
            </ol>
          </div>
        </nav>

        {/* Trust Strip */}
        <section className="bg-gray-950 border-b border-cyan-500/20 py-5">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="flex items-center gap-3">
                <div className="bg-cyan-500/10 border border-cyan-500/30 rounded-lg p-2 flex-shrink-0">
                  <Award className="text-cyan-400" size={22} />
                </div>
                <div>
                  <p className="text-white font-semibold text-sm leading-tight">13 Years of Experience</p>
                  <p className="text-gray-400 text-xs">PPF, ceramic & wraps</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="bg-cyan-500/10 border border-cyan-500/30 rounded-lg p-2 flex-shrink-0">
                  <Shield className="text-cyan-400" size={22} />
                </div>
                <div>
                  <p className="text-white font-semibold text-sm leading-tight">Premium Materials</p>
                  <p className="text-gray-400 text-xs">Warranty-backed films & coatings</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="bg-cyan-500/10 border border-cyan-500/30 rounded-lg p-2 flex-shrink-0">
                  <MapPin className="text-cyan-400" size={22} />
                </div>
                <div>
                  <p className="text-white font-semibold text-sm leading-tight">Vista / North County SD</p>
                  <p className="text-gray-400 text-xs">Serving all San Diego County</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="bg-cyan-500/10 border border-cyan-500/30 rounded-lg p-2 flex-shrink-0">
                  <Sparkles className="text-cyan-400" size={22} />
                </div>
                <div>
                  <p className="text-white font-semibold text-sm leading-tight">Tesla · Luxury · Exotic</p>
                  <p className="text-gray-400 text-xs">Specialists in high-value vehicles</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Quick Summary */}
        <section className="py-16 bg-gray-900 border-y border-cyan-500/20">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold mb-3 text-white text-center">PPF vs Ceramic Coating — Quick Answer</h2>
            <p className="text-gray-400 text-center mb-10 max-w-3xl mx-auto">If you only read one section, read this. Here is the fast, decision-ready summary of how Paint Protection Film and ceramic coating compare.</p>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="flex items-start gap-3 bg-black p-5 rounded-xl border border-cyan-500/20">
                <Shield className="text-cyan-400 mt-1 flex-shrink-0" size={22} />
                <p className="text-gray-300"><strong className="text-white">What PPF is:</strong> Paint Protection Film is a thick, clear (or colored) urethane film applied over your paint. It physically absorbs rock chips, scratches, and impact, and self-heals light marks with heat.</p>
              </div>
              <div className="flex items-start gap-3 bg-black p-5 rounded-xl border border-cyan-500/20">
                <Droplets className="text-cyan-400 mt-1 flex-shrink-0" size={22} />
                <p className="text-gray-300"><strong className="text-white">What ceramic coating is:</strong> Ceramic coating is a liquid polymer that chemically bonds to the surface, creating a hydrophobic, UV-resistant, glossy layer that makes the vehicle easier to clean.</p>
              </div>
              <div className="flex items-start gap-3 bg-black p-5 rounded-xl border border-cyan-500/20">
                <Zap className="text-cyan-400 mt-1 flex-shrink-0" size={22} />
                <p className="text-gray-300"><strong className="text-white">Which protects the paint:</strong> PPF is the clear winner for physical paint protection. It is the only one of the two that stops rock chips, road debris, and deep scratches.</p>
              </div>
              <div className="flex items-start gap-3 bg-black p-5 rounded-xl border border-cyan-500/20">
                <Sparkles className="text-cyan-400 mt-1 flex-shrink-0" size={22} />
                <p className="text-gray-300"><strong className="text-white">Which is easier to maintain:</strong> Ceramic coating wins on maintenance. Its hydrophobic surface repels water and dirt, so washes are faster and the finish stays glossy with less effort.</p>
              </div>
              <div className="flex items-start gap-3 bg-black p-5 rounded-xl border border-cyan-500/20 md:col-span-2">
                <Check className="text-cyan-400 mt-1 flex-shrink-0" size={22} />
                <p className="text-gray-300"><strong className="text-white">When to choose which:</strong> Choose <strong className="text-white">PPF</strong> if your priority is protecting paint from chips and scratches (new cars, Teslas, highway driving, long-term ownership). Choose <strong className="text-white">ceramic coating</strong> if your priority is gloss and easy cleaning at a lower cost. Choose <strong className="text-white">both</strong> for the most complete, layered protection — PPF protects the paint, ceramic protects the PPF.</p>
              </div>
            </div>
          </div>
        </section>

        {/* Detailed Comparison Table */}
        <section className="py-20 bg-black">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-4xl font-bold mb-4 text-white text-center">PPF vs Ceramic Coating — Full Comparison Table</h2>
            <p className="text-gray-400 text-center mb-10 max-w-3xl mx-auto">A side-by-side breakdown of how Paint Protection Film and ceramic coating compare across every factor that matters to San Diego drivers.</p>
            <div className="overflow-x-auto rounded-xl border border-cyan-500/20">
              <table className="w-full text-left border-collapse">
                <thead>
                  <tr className="bg-gray-900">
                    <th className="p-4 text-white font-bold border-b border-cyan-500/20">Factor</th>
                    <th className="p-4 text-orange-400 font-bold border-b border-cyan-500/20">Paint Protection Film (PPF)</th>
                    <th className="p-4 text-cyan-400 font-bold border-b border-cyan-500/20">Ceramic Coating</th>
                  </tr>
                </thead>
                <tbody className="text-sm">
                  {[
                    ['Paint Protection', 'Excellent — thick physical barrier', 'Light — chemical surface layer only'],
                    ['Rock Chip Protection', 'Yes — absorbs impact & debris', 'No — does not stop chips'],
                    ['Scratch Resistance', 'High — resists & self-heals light scratches', 'Moderate — resists swirls & wash marks'],
                    ['Self-Healing', 'Yes — heals with heat', 'No'],
                    ['UV Protection', 'Excellent', 'Excellent'],
                    ['Hydrophobic Properties', 'Good (better with ceramic topcoat)', 'Excellent — strong water beading'],
                    ['Longevity', '7–10 years (warranty-backed)', '2–5 years'],
                    ['Maintenance', 'Low — easy to wash', 'Very low — self-cleaning effect'],
                    ['Appearance Enhancement', 'Gloss or matte; clear or color options', 'Deep, glass-like gloss & shine'],
                    ['Cost', 'Higher investment', 'Lower investment'],
                    ['Warranty', 'Manufacturer warranty (up to 10 yrs)', 'Typically 2–5 year service warranty'],
                    ['Best Use Case', 'Physical rock chip & impact protection', 'Gloss, UV & easy-clean maintenance'],
                  ].map((row, i) => (
                    <tr key={i} className={i % 2 === 0 ? 'bg-black' : 'bg-gray-900/50'}>
                      <td className="p-4 text-white font-semibold border-b border-gray-800">{row[0]}</td>
                      <td className="p-4 text-gray-300 border-b border-gray-800">{row[1]}</td>
                      <td className="p-4 text-gray-300 border-b border-gray-800">{row[2]}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="text-center text-gray-400 mt-8">
              Bottom line: <strong className="text-white">PPF</strong> wins on physical protection and longevity; <strong className="text-white">ceramic coating</strong> wins on gloss, hydrophobic maintenance, and value. The two are not rivals — they are partners.
            </p>
          </div>
        </section>

        {/* What Is PPF */}
        <section className="py-20 bg-gray-900">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-4xl font-bold mb-6 text-white text-center">What Is Paint Protection Film?</h2>
            <div className="space-y-5 text-gray-300 leading-relaxed text-lg">
              <p>
                Paint Protection Film (PPF) is a thick, durable, optically clear urethane film that is precisely applied over your vehicle's painted surfaces. Originally developed to protect military helicopter blades from debris, PPF is now the gold standard for protecting automotive paint from <strong className="text-white">rock chips, road debris, scratches, bug acids, and minor abrasions</strong>.
              </p>
              <p>
                Modern PPF is <strong className="text-white">self-healing</strong> — light scratches and swirl marks disappear with heat from the sun or warm water. It is typically around 8 mils thick, acting as a genuine physical barrier between hazards and your paint. PPF can be applied as a full-front package, full-body coverage, or on high-impact areas like the hood, fenders, mirrors, and bumper.
              </p>
              <p>
                Because it physically shields the paint, PPF is the single most effective way to preserve a vehicle's factory finish and resale value — which is why it is so popular for new cars, leased vehicles, Teslas, and luxury and exotic vehicles in San Diego.
              </p>
            </div>
            <div className="mt-8 bg-black p-6 rounded-xl border border-cyan-500/20 text-center">
              <p className="text-gray-300">
                Learn more about <a href="/paint-protection-film-san-diego" className="text-cyan-400 hover:underline font-semibold">Paint Protection Film San Diego</a>, or explore <a href="/colored-ppf-san-diego" className="text-cyan-400 hover:underline font-semibold">Colored Paint Protection Film</a> if you want protection plus a color change in one product.
              </p>
            </div>
          </div>
        </section>

        {/* What Is Ceramic Coating */}
        <section className="py-20 bg-black">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-4xl font-bold mb-6 text-white text-center">What Is Ceramic Coating?</h2>
            <div className="space-y-5 text-gray-300 leading-relaxed text-lg">
              <p>
                Ceramic coating is a liquid polymer — made primarily of silica (SiO2) and nano-ceramic compounds — that chemically bonds to your vehicle's surface. Once cured, it forms a durable, semi-permanent layer that becomes part of the surface rather than sitting on top like wax.
              </p>
              <p>
                A ceramic coating is <strong className="text-white">hydrophobic</strong> (it repels water so dirt slides off), <strong className="text-white">UV-resistant</strong> (it fights sun fading), and <strong className="text-white">chemically resistant</strong> (it shrugs off bird droppings, bug acids, and fallout that would otherwise etch paint). It also delivers a deep, glass-like gloss and makes the vehicle dramatically easier to clean.
              </p>
              <p>
                What ceramic coating does <strong className="text-white">not</strong> do is stop rock chips. It is only microns thick — a chemical shield, not a physical one. That is the key reason it is best paired with, not substituted for, Paint Protection Film on high-impact areas.
              </p>
            </div>
            <div className="mt-8 bg-gray-900 p-6 rounded-xl border border-cyan-500/20 text-center">
              <p className="text-gray-300">
                Read the full <a href="/ceramic-coating-san-diego" className="text-cyan-400 hover:underline font-semibold">Ceramic Coating San Diego</a> guide to understand benefits, longevity, and how ceramic coating protects paint, wraps, and PPF.
              </p>
            </div>
          </div>
        </section>

        {/* When PPF Is Better */}
        <section className="py-20 bg-gray-900">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-4xl font-bold mb-4 text-white text-center">When Paint Protection Film Is the Better Choice</h2>
            <p className="text-gray-400 text-center mb-12 max-w-3xl mx-auto">Choose PPF when physical protection of your paint is the priority. Here are the situations where PPF is clearly the right call.</p>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[
                { icon: Car, title: 'Highway & Freeway Driving', body: 'If you log serious miles on I-5, I-15, or the 78, your front end takes constant rock chip and debris abuse. PPF is the only product that physically stops that damage.' },
                { icon: Zap, title: 'Teslas & Soft Factory Paint', body: 'Tesla paint is notoriously thin and soft. PPF protects vulnerable panels from chipping — the top reason Tesla owners in San Diego invest in film.' },
                { icon: Sparkles, title: 'New & Leased Vehicles', body: 'Protecting a new or leased car from day one preserves the factory finish, avoids chip damage, and helps protect resale or lease turn-in value.' },
                { icon: Shield, title: 'Rock Chip Protection', body: 'If your number one concern is chips, scratches, and impact damage, PPF is non-negotiable. No coating can match a physical urethane barrier.' },
                { icon: Clock, title: 'Long-Term Ownership', body: 'Planning to keep your vehicle for years? PPF lasts 7–10 years and is warranty-backed, making it the smart long-term protection investment.' },
                { icon: Award, title: 'Luxury & Exotic Vehicles', body: 'High-value paint deserves real protection. PPF shields expensive finishes on supercars, luxury sedans, and collector vehicles from costly chip repairs.' },
              ].map((item, i) => (
                <div key={i} className="bg-black p-7 rounded-xl border border-orange-500/30">
                  <div className="bg-orange-500/10 border border-orange-500/30 rounded-lg p-2 w-fit mb-4">
                    <item.icon className="text-orange-400" size={22} />
                  </div>
                  <h3 className="text-white font-bold mb-2">{item.title}</h3>
                  <p className="text-gray-400 text-sm leading-relaxed">{item.body}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* When Ceramic Is Better */}
        <section className="py-20 bg-black">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-4xl font-bold mb-4 text-white text-center">When Ceramic Coating Is the Better Choice</h2>
            <p className="text-gray-400 text-center mb-12 max-w-3xl mx-auto">Choose ceramic coating when gloss, easy maintenance, and value are the priority. Here is when ceramic is the smarter pick.</p>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[
                { icon: Droplets, title: 'Easier Washing & Maintenance', body: 'The hydrophobic surface causes water and dirt to bead and roll off. Washes are faster, water spots are fewer, and the car stays cleaner longer.' },
                { icon: Sparkles, title: 'Gloss & Shine Enhancement', body: 'Ceramic coating adds remarkable depth and a wet, glass-like shine. Colors look richer and the finish stays brilliant for years.' },
                { icon: Zap, title: 'Lower Investment', body: 'Ceramic coating costs significantly less than PPF, making it an affordable way to upgrade protection and appearance on any budget.' },
                { icon: Car, title: 'Existing & Daily-Driver Vehicles', body: 'For a car already in service that you want to keep glossy and low-maintenance, ceramic coating is a fast, cost-effective upgrade.' },
                { icon: Sun, title: 'UV & Oxidation Defense', body: 'In San Diego sun, ceramic coating blocks UV rays that fade and oxidize paint and wraps, keeping the finish vibrant longer.' },
                { icon: Check, title: 'Maintenance-Focused Owners', body: 'If your goal is spending less time cleaning and more time enjoying a sharp-looking vehicle, ceramic coating delivers the biggest convenience boost.' },
              ].map((item, i) => (
                <div key={i} className="bg-gray-900 p-7 rounded-xl border border-cyan-500/30">
                  <div className="bg-cyan-500/10 border border-cyan-500/30 rounded-lg p-2 w-fit mb-4">
                    <item.icon className="text-cyan-400" size={22} />
                  </div>
                  <h3 className="text-white font-bold mb-2">{item.title}</h3>
                  <p className="text-gray-400 text-sm leading-relaxed">{item.body}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Why Many Choose Both */}
        <section className="py-20 bg-gradient-to-br from-gray-900 via-black to-gray-900 border-y border-cyan-500/20">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <div className="inline-flex items-center gap-2 bg-cyan-500/10 border border-cyan-500/30 rounded-full px-4 py-1.5 mb-5">
                <Layers className="text-cyan-400" size={18} />
                <span className="text-cyan-400 text-sm font-semibold">The Most Popular Choice</span>
              </div>
              <h2 className="text-4xl font-bold mb-4 text-white">Why Many Customers Choose Both PPF and Ceramic Coating</h2>
              <p className="text-gray-300 text-lg max-w-3xl mx-auto">The smartest protection strategy is not choosing one over the other — it is layering them. Here is why the PPF-plus-ceramic combination is the go-to package for San Diego's most discerning owners.</p>
            </div>
            <div className="grid md:grid-cols-2 gap-6 mb-10">
              <div className="bg-black p-7 rounded-xl border border-orange-500/30">
                <Shield className="text-orange-400 mb-4" size={28} />
                <h3 className="text-xl font-bold text-white mb-2">PPF Protects the Paint</h3>
                <p className="text-gray-400 leading-relaxed">Paint Protection Film forms the physical first line of defense, absorbing rock chips, scratches, and impact so they never reach your factory paint.</p>
              </div>
              <div className="bg-black p-7 rounded-xl border border-cyan-500/30">
                <Droplets className="text-cyan-400 mb-4" size={28} />
                <h3 className="text-xl font-bold text-white mb-2">Ceramic Coating Protects the PPF</h3>
                <p className="text-gray-400 leading-relaxed">A ceramic coating applied over the PPF adds hydrophobic, easy-clean, UV-resistant properties — protecting and beautifying the film itself while boosting gloss.</p>
              </div>
            </div>
            <div className="bg-black/60 p-8 rounded-xl border border-cyan-500/20">
              <h3 className="text-2xl font-bold text-white mb-4 text-center">How the Two Work Together</h3>
              <div className="space-y-4 text-gray-300 leading-relaxed">
                <p>
                  Think of it as layered armor. <strong className="text-white">PPF</strong> handles the physical threats — the chips, the debris, the scratches. <strong className="text-white">Ceramic coating</strong> handles the surface experience — the gloss, the water beading, the easy cleaning, and the UV defense. Together they deliver protection that neither product can provide alone.
                </p>
                <p>
                  A common San Diego setup is <strong className="text-white">full-front or full-body PPF</strong> on a Tesla or luxury vehicle, finished with a <strong className="text-white">ceramic coating over the entire car</strong> — film and paint alike. The result is a vehicle that resists chips, shrugs off the sun, beads water beautifully, and takes minutes to wash.
                </p>
                <p>
                  This combination is especially popular with <strong className="text-white">Tesla owners</strong> and <strong className="text-white">luxury and exotic vehicle owners</strong> who want zero compromises — maximum paint protection and a flawless, low-maintenance finish.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Mid-page CTA */}
        <section className="py-14 bg-gradient-to-r from-cyan-600 to-cyan-500">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-3xl font-bold mb-4 text-white">Not Sure Which Is Right for Your Vehicle?</h2>
            <p className="text-lg text-white/90 mb-6">Our Vista team will assess your vehicle, driving habits, and goals, then recommend PPF, ceramic coating, or the right combination of both.</p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a href="tel:858-282-8119" className="bg-white hover:bg-gray-100 text-gray-900 font-bold py-3 px-10 rounded-lg transition inline-flex items-center justify-center gap-2">
                <Phone size={20} />
                Call 858-282-8119
              </a>
              <button onClick={() => { navigate('/contact'); setTimeout(() => window.scrollTo(0, 0), 100) }} className="bg-orange-500 hover:bg-orange-600 text-white font-bold py-3 px-10 rounded-lg transition inline-flex items-center justify-center gap-2">
                <Calendar size={20} />
                Get Free Quote
              </button>
            </div>
          </div>
        </section>

        {/* Tesla Section */}
        <section className="py-20 bg-black">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-4xl font-bold mb-4 text-white text-center">PPF vs Ceramic Coating for Teslas</h2>
            <p className="text-gray-400 text-center mb-12 max-w-3xl mx-auto">Tesla factory paint is soft and chips easily, which makes the PPF vs ceramic coating decision especially important for Model Y, Model 3, Model S, and Model X owners in San Diego.</p>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-10">
              {[
                { model: 'Tesla Model Y', body: 'San Diego\'s most popular EV. Full-front PPF protects the hood, fascia, and mirrors from chips; add ceramic coating for gloss and effortless washing.' },
                { model: 'Tesla Model 3', body: 'A daily-driver favorite that racks up freeway miles. PPF guards the front end from rock chips, while ceramic keeps the finish slick and clean.' },
                { model: 'Tesla Model S', body: 'A premium sedan deserving full protection. Many owners choose full-body PPF plus ceramic coating to keep the paint flawless for years.' },
                { model: 'Tesla Model X', body: 'A high-value family EV. PPF protects high-impact panels and the falcon-wing door edges; ceramic coating adds gloss and easy maintenance.' },
              ].map((item, i) => (
                <div key={i} className="bg-gray-900 p-6 rounded-xl border border-cyan-500/20">
                  <Zap className="text-cyan-400 mb-3" size={24} />
                  <h3 className="text-white font-bold mb-2">{item.model}</h3>
                  <p className="text-gray-400 text-sm leading-relaxed">{item.body}</p>
                </div>
              ))}
            </div>
            <div className="bg-gray-900 p-7 rounded-xl border border-cyan-500/20 text-center">
              <p className="text-gray-300 text-lg">
                <strong className="text-white">The Tesla owner's answer:</strong> most choose PPF on high-impact areas (or full coverage) for chip protection, then a ceramic coating on top for shine and easy cleaning.
              </p>
              <p className="text-gray-400 mt-4">
                Explore <a href="/tesla-ppf-san-diego" className="text-cyan-400 hover:underline font-semibold">Tesla PPF San Diego</a> for paint protection film tailored to Tesla's soft paint, or <a href="/tesla-wrap-san-diego" className="text-cyan-400 hover:underline font-semibold">Tesla Wrap San Diego</a> for color change and styling options.
              </p>
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-20 bg-gray-900">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-4xl font-bold mb-4 text-white text-center">PPF vs Ceramic Coating FAQs — San Diego</h2>
            <p className="text-gray-400 text-center mb-12 max-w-2xl mx-auto">The most common questions San Diego drivers ask when deciding between Paint Protection Film and ceramic coating.</p>
            <div className="space-y-5">
              {[
                {
                  q: 'Is PPF better than ceramic coating?',
                  a: 'Neither is universally "better" — they do different jobs. PPF is a thick film that physically blocks rock chips, scratches, and impact, and it self-heals light marks. Ceramic coating is a thin chemical layer that adds gloss, UV protection, and hydrophobic easy-cleaning but does not stop rock chips. For physical defense, PPF wins; for shine and maintenance, ceramic wins. Many San Diego owners use both together.'
                },
                {
                  q: 'Do I need both PPF and ceramic coating?',
                  a: 'You do not strictly need both, but combining them gives the most complete protection. PPF protects your paint from rock chips and impact, while a ceramic coating over the PPF and the rest of the vehicle adds gloss, UV resistance, and an easy-clean hydrophobic surface. This combination is the most popular choice among Tesla and luxury owners in San Diego.'
                },
                {
                  q: 'Does ceramic coating stop rock chips?',
                  a: 'No. Ceramic coating is only microns thick and is a chemical layer, not a physical barrier. It resists light swirls, chemical stains, and UV damage, but it cannot stop rock chips, road debris, or deep scratches. Only Paint Protection Film (PPF) provides genuine rock chip and impact protection.'
                },
                {
                  q: 'Can ceramic coating replace PPF?',
                  a: 'No. Ceramic coating and PPF solve different problems. Ceramic coating cannot replace the physical rock chip and impact protection that Paint Protection Film provides. If your main goal is preventing chips, scratches, and road damage, you need PPF. Ceramic coating is best used as a complement — often applied over PPF — rather than a substitute.'
                },
                {
                  q: 'Which lasts longer, PPF or ceramic coating?',
                  a: 'PPF lasts longer. Quality Paint Protection Film typically lasts 7 to 10 years and often carries a manufacturer warranty. Ceramic coating generally lasts 2 to 5 years depending on the product and care. Because PPF is more durable and warranty-backed, it is the longer-term protection investment, while ceramic is refreshed more frequently.'
                },
                {
                  q: 'Which is worth the money, PPF or ceramic coating?',
                  a: 'It depends on your goals. PPF is worth it if you want to protect paint from rock chips and preserve resale value on a new, leased, or long-term vehicle — especially Teslas and luxury cars. Ceramic coating is worth it if you want a glossy, low-maintenance, easy-to-clean finish at a lower investment. For the best long-term value, many San Diego owners invest in PPF on high-impact areas and ceramic coating over the whole vehicle.'
                },
                {
                  q: 'What do most Tesla owners choose, PPF or ceramic coating?',
                  a: 'Most Tesla owners in San Diego choose PPF on high-impact areas — or full-front and full-body PPF — because Tesla factory paint is soft and chips easily, then add a ceramic coating on top for gloss and easy maintenance. The PPF-plus-ceramic combination is the go-to package for Model Y, Model 3, Model S, and Model X owners.'
                },
                {
                  q: 'Can ceramic coating be applied over PPF?',
                  a: 'Yes — and it is highly recommended. Applying a ceramic coating over Paint Protection Film enhances the film\'s hydrophobic properties, reduces staining, boosts gloss, and makes the PPF easier to clean. The ceramic layer acts as a sacrificial, easy-maintenance topcoat that protects and beautifies the PPF underneath.'
                }
              ].map((item, i) => (
                <div key={i} className="bg-black p-6 rounded-xl border border-cyan-500/20">
                  <h3 className="text-xl font-bold mb-3 text-white">{item.q}</h3>
                  <p className="text-gray-300 leading-relaxed">{item.a}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Why Choose Mr Wrap */}
        <section className="py-20 bg-black">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-4xl font-bold mb-12 text-white text-center">Why Choose Mr Wrap for PPF & Ceramic Coating in San Diego</h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-5xl mx-auto">
              {[
                { icon: Award, title: '13 Years of Experience', body: '13 years of professional PPF, ceramic coating, and wrap installation for San Diego vehicles. Experience shows in every install.' },
                { icon: MapPin, title: 'Vista / North County SD Location', body: 'Located at 2244 South Santa Fe Avenue, Suite A7, Vista, CA 92084 — easy access from Oceanside, Carlsbad, San Marcos, Encinitas, and greater San Diego.' },
                { icon: Shield, title: 'Premium, Warranty-Backed Materials', body: 'We use only top-tier paint protection films and professional-grade ceramic coatings — never consumer products — for genuine, lasting protection.' },
                { icon: Sparkles, title: 'Tesla, Luxury & Exotic Specialists', body: 'From Tesla\'s soft paint to exotic supercars, we have specific experience with the high-value vehicles San Diego drivers care about most.' },
                { icon: Layers, title: 'Complete Layered Protection Packages', body: 'We design PPF-plus-ceramic packages tailored to your vehicle, driving habits, and budget — so you get exactly the protection you need.' },
                { icon: Check, title: 'Honest, No-Pressure Guidance', body: 'We will tell you when PPF, ceramic coating, or both make sense — and when they do not. Straight answers, quality work, fair pricing.' }
              ].map((item, i) => (
                <div key={i} className="bg-gray-900 p-8 rounded-xl border border-cyan-500/20">
                  <div className="bg-cyan-500/20 w-14 h-14 rounded-full flex items-center justify-center mb-5 border border-cyan-500">
                    <item.icon className="text-cyan-400" size={28} />
                  </div>
                  <h3 className="text-xl font-bold mb-3 text-white">{item.title}</h3>
                  <p className="text-gray-300 text-sm leading-relaxed">{item.body}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Service Area */}
        <section className="py-16 bg-gray-900">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold mb-8 text-white text-center flex items-center justify-center gap-3">
              <MapPin className="text-cyan-400" size={30} />
              PPF & Ceramic Coating — Serving San Diego County
            </h2>
            <p className="text-gray-300 text-lg mb-8 text-center">
              Based in Vista, we install premium Paint Protection Film and ceramic coatings throughout San Diego County and North County.
            </p>
            <div className="grid md:grid-cols-3 gap-4 text-gray-300 text-sm">
              <div className="bg-black p-4 rounded-lg border border-cyan-500/20">
                <h3 className="font-bold text-white mb-2">North County Coastal</h3>
                <ul className="space-y-1">
                  <li>• Oceanside</li>
                  <li>• Carlsbad</li>
                  <li>• Encinitas</li>
                  <li>• Del Mar</li>
                </ul>
              </div>
              <div className="bg-black p-4 rounded-lg border border-cyan-500/20">
                <h3 className="font-bold text-white mb-2">North County Inland</h3>
                <ul className="space-y-1">
                  <li>• Vista (Shop Location)</li>
                  <li>• San Marcos</li>
                  <li>• Escondido</li>
                  <li>• Rancho Santa Fe</li>
                  <li>• Poway</li>
                </ul>
              </div>
              <div className="bg-black p-4 rounded-lg border border-cyan-500/20">
                <h3 className="font-bold text-white mb-2">Greater San Diego</h3>
                <ul className="space-y-1">
                  <li>• San Diego</li>
                  <li>• La Jolla</li>
                  <li>• North County San Diego</li>
                  <li>• San Diego County</li>
                </ul>
              </div>
            </div>
            <p className="text-gray-300 text-center mt-8">
              <strong className="text-white">Shop Address:</strong> 2244 South Santa Fe Avenue, Suite A7, Vista, CA 92084
            </p>
          </div>
        </section>

        {/* Before & After — PPF vs Ceramic visual proof */}
        <section className="py-20 bg-gray-950">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold mb-4 text-white">See the Difference — Real Results</h2>
              <p className="text-xl text-gray-400">Drag the sliders to compare before and after. Real projects from our Vista shop.</p>
            </div>

            {/* PPF protection story — closes the decision for undecided visitors */}
            <div className="bg-gray-900 rounded-2xl border border-cyan-500/20 p-6 mb-8">
              <div className="mb-4">
                <span className="inline-block bg-orange-500/10 border border-orange-500/30 text-orange-400 text-xs font-bold px-3 py-1 rounded-full mb-3">WHY PPF — REAL PROTECTION PROOF</span>
                <h3 className="text-2xl font-bold text-white">This Is What Ceramic Coating Cannot Do</h3>
                <p className="text-gray-400 mt-2 text-sm">This Range Rover's satin PPF absorbed real road impact. The ceramic coating would have allowed the damage to reach the paint. PPF caught it, was cleaned and repaired — the factory paint underneath was untouched. This is the difference between physical protection and a surface coating.</p>
              </div>
              <BeforeAfterSlider
                beforeImage="placeholder:range-rover-ppf-damage-caught"
                afterImage="https://static.lumi.new/3f/3f55d3a9c3a7cb148dc864f4654c6428.webp"
                beforeLabel="Damage Caught by PPF"
                afterLabel="Paint Preserved"
                beforeAlt="Range Rover PPF caught road damage — paint underneath protected"
                afterAlt="Range Rover satin PPF — Mr. Wrap Vista"
                projectTitle="Range Rover — Full Body Satin PPF · Vista, CA"
                serviceBadge="Full Body Satin PPF"
                showCta
                className="max-w-3xl mx-auto"
              />
            </div>

            {/* Two-column: ceramic gloss vs PPF install */}
            <div className="grid md:grid-cols-2 gap-6">
              <div className="bg-gray-900 rounded-2xl border border-cyan-500/20 p-5">
                <span className="inline-block bg-cyan-500/10 border border-cyan-500/30 text-cyan-400 text-xs font-bold px-3 py-1 rounded-full mb-3">CERAMIC COATING</span>
                <h3 className="text-lg font-bold text-white mb-1">Tesla Model 3 — PPF + Ceramic Combo</h3>
                <p className="text-gray-400 text-sm mb-4">Full front PPF for chip protection, then ceramic over the whole car for glass-like gloss and easy cleaning — the most popular Tesla package.</p>
                <BeforeAfterSlider
                  beforeImage="placeholder:tesla-ppf-ceramic-before"
                  afterImage="https://static.lumi.new/c5/c569c3f9888a7a4fed4a4801f7c73daa.webp"
                  beforeLabel="Before"
                  afterLabel="PPF + Ceramic"
                  beforeAlt="Tesla Model 3 before PPF and ceramic coating"
                  afterAlt="Tesla Model 3 after PPF and ceramic coating"
                  projectTitle="Tesla Model 3 — Full Front PPF + Ceramic"
                  serviceBadge="PPF + Ceramic Package"
                />
              </div>
              <div className="bg-gray-900 rounded-2xl border border-cyan-500/20 p-5">
                <span className="inline-block bg-orange-500/10 border border-orange-500/30 text-orange-400 text-xs font-bold px-3 py-1 rounded-full mb-3">PPF</span>
                <h3 className="text-lg font-bold text-white mb-1">Porsche Macan — Full Body PPF</h3>
                <p className="text-gray-400 text-sm mb-4">Installation in progress vs. the invisible finished result. PPF protects every panel while the car looks completely factory-stock.</p>
                <BeforeAfterSlider
                  beforeImage="placeholder:porsche-macan-ppf-process"
                  afterImage="https://static.lumi.new/52/5234166494ce9fbf1dd6bad26161ad9e.webp"
                  beforeLabel="Install In Progress"
                  afterLabel="Finished"
                  beforeAlt="Porsche Macan during PPF installation"
                  afterAlt="Porsche Macan completed white PPF"
                  projectTitle="Porsche Macan — Full Vehicle PPF"
                  serviceBadge="Full Body PPF"
                />
              </div>
            </div>

            <p className="text-center text-gray-500 text-xs mt-6">
              Real customer before &amp; after photos uploading soon. Call or text <a href="tel:858-282-8119" className="text-cyan-400 hover:underline">858-282-8119</a> to discuss your project.
            </p>
          </div>
        </section>

        {/* Embedded Quote Form */}
        <section className="py-16 bg-gray-950">
          <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
            <EmbeddedQuoteForm
              defaultService="other"
              heading="Get a Free PPF or Ceramic Coating Quote"
              subheading="Tell us about your vehicle and goals. Our Vista team will recommend PPF, ceramic coating, or the right combination — and provide a personalized quote."
            />
          </div>
        </section>

        {/* Related Services */}
        <section className="py-16 bg-black">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold mb-8 text-white text-center">Related Services</h2>
            <div className="grid md:grid-cols-3 gap-5">
              {[
                { href: '/paint-protection-film-san-diego', title: 'Paint Protection Film San Diego', desc: 'Clear self-healing PPF for ultimate rock chip and UV protection' },
                { href: '/ceramic-coating-san-diego', title: 'Ceramic Coating San Diego', desc: 'Hydrophobic, UV-resistant ceramic coating for paint, wraps, and PPF' },
                { href: '/colored-ppf-san-diego', title: 'Colored PPF San Diego', desc: 'Color change paint protection film — protection plus a new finish' },
                { href: '/tesla-ppf-san-diego', title: 'Tesla PPF San Diego', desc: 'Paint protection film tailored for Tesla\'s soft factory paint' },
                { href: '/tesla-wrap-san-diego', title: 'Tesla Wrap San Diego', desc: 'Dedicated Tesla wrap services — stealth, satin, and color change' },
                { href: '/vehicle-wrap-cost-san-diego', title: 'Vehicle Wrap Cost San Diego', desc: 'The definitive guide to vehicle wrap pricing in San Diego' }
              ].map((item, i) => (
                <a key={i} href={item.href} className="bg-gray-900 p-6 rounded-xl border border-cyan-500/20 hover:border-cyan-500 transition group">
                  <h3 className="text-lg font-bold text-white mb-2 group-hover:text-cyan-400 transition">{item.title}</h3>
                  <p className="text-gray-400 text-sm">{item.desc}</p>
                </a>
              ))}
            </div>
          </div>
        </section>

        {/* Final CTA */}
        <section className="py-20 bg-gradient-to-r from-cyan-500 to-cyan-600">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-4xl font-bold mb-6 text-white">PPF, Ceramic Coating, or Both? Let's Find Out.</h2>
            <p className="text-xl text-white/90 mb-4">
              Whether you want maximum paint protection, an effortless glossy finish, or the best of both, our Vista team will recommend the perfect package for your vehicle.
            </p>
            <p className="text-lg text-white/80 mb-8">
              Call <strong>858-282-8119</strong> or request a free quote online — no pressure, no obligation.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button onClick={() => { navigate('/contact'); setTimeout(() => window.scrollTo(0, 0), 100) }} className="bg-orange-500 hover:bg-orange-600 text-white font-bold py-4 px-12 rounded-lg transition-all transform hover:scale-105 shadow-xl inline-flex items-center justify-center gap-2">
                <Calendar size={20} />
                Get Free Quote
              </button>
              <a href="tel:858-282-8119" className="bg-white hover:bg-gray-100 text-gray-900 font-bold py-4 px-12 rounded-lg transition-all transform hover:scale-105 shadow-xl inline-flex items-center justify-center gap-2">
                <Phone size={20} />
                Call: 858-282-8119
              </a>
            </div>
          </div>
        </section>

      </div>
    </>
  )
}

export default PpfVsCeramicCoatingSanDiegoPage
